import { Utils } from "@engaging-tech/components"

import { createFonts } from "../../../theme"

function buildBranchTreeIdMap(questions, branchingLogic, orderedQuestionIds) {
  const qids = Object.keys(questions)

  const orderIndex = {}
  if (Array.isArray(orderedQuestionIds)) {
    orderedQuestionIds.forEach((id, idx) => {
      orderIndex[id] = idx
    })
  }

  const branchingUniverse = new Set()

  if (Array.isArray(branchingLogic)) {
    branchingLogic.forEach(r => {
      const from = r.currentQuestion
      const to = r.targetQuestion

      if (from && questions[from]) branchingUniverse.add(from)
      if (to && questions[to]) branchingUniverse.add(to)

      const iFrom = orderIndex[from]
      const iTo = orderIndex[to]

      if (Number.isInteger(iFrom) && Number.isInteger(iTo) && iTo > iFrom) {
        for (let i = iFrom + 1; i < iTo; i++) {
          const mid = orderedQuestionIds[i]
          if (mid && questions[mid]) branchingUniverse.add(mid)
        }
      }
    })
  }

  const undirected = {}
  qids.forEach(id => {
    undirected[id] = new Set()
  })

  const branchingOut = {}
  qids.forEach(id => {
    branchingOut[id] = new Set()
  })

  if (Array.isArray(branchingLogic)) {
    branchingLogic.forEach(r => {
      const from = r.currentQuestion
      const to = r.targetQuestion
      if (!from || !to) return
      if (!questions[from] || !questions[to]) return

      undirected[from].add(to)
      undirected[to].add(from)

      branchingOut[from].add(to)
    })
  }

  if (Array.isArray(orderedQuestionIds)) {
    for (let i = 0; i < orderedQuestionIds.length - 1; i++) {
      const a = orderedQuestionIds[i]
      const b = orderedQuestionIds[i + 1]
      if (!questions[a] || !questions[b]) continue
      if (!branchingUniverse.has(a) || !branchingUniverse.has(b)) continue

      undirected[a].add(b)
      undirected[b].add(a)
    }
  }

  const visited = new Set()
  function getComponent(start) {
    const stack = [start]
    const comp = []
    while (stack.length) {
      const node = stack.pop()
      if (visited.has(node)) continue
      visited.add(node)
      comp.push(node)
      undirected[node].forEach(n => {
        if (!visited.has(n)) stack.push(n)
      })
    }
    return comp
  }

  const branchTreeIdMap = {}
  qids.forEach(id => {
    branchTreeIdMap[id] = null
  })

  let treeCounter = 0

  qids.forEach(startId => {
    if (visited.has(startId)) return

    const component = getComponent(startId)

    const hasEdges = component.some(id => undirected[id].size > 0)
    const hasUniverse = component.some(id => branchingUniverse.has(id))
    if (!hasEdges || !hasUniverse) return

    const treeId = `BT_${++treeCounter}`
    const inComp = new Set(component)

    component.forEach(id => {
      const q = questions[id]
      if (!q) return
      if (!branchingUniverse.has(id)) return
      if (q.isCore) return
      branchTreeIdMap[id] = treeId
    })

    const indegreeFromBranching = {}
    component.forEach(id => {
      indegreeFromBranching[id] = 0
    })

    if (Array.isArray(branchingLogic)) {
      branchingLogic.forEach(r => {
        const from = r.currentQuestion
        const to = r.targetQuestion
        if (!from || !to) return
        if (!inComp.has(from) || !inComp.has(to)) return
        indegreeFromBranching[to] += 1
      })
    }

    // Null “merge & not-driver” ONLY for non-dropdown (Rule B)
    component.forEach(id => {
      const q = questions[id]
      if (!q) return
      if (q.isCore) return
      if (!branchingUniverse.has(id)) return

      const isMerge = indegreeFromBranching[id] >= 2
      const isDriver = branchingOut[id] && branchingOut[id].size > 0

      // Rule B: only non-dropdown and non-checkbox merges become null
      if (isMerge && !isDriver && q.type !== "dropdown" && q.type !== "checkbox") {
        branchTreeIdMap[id] = null
      }
    })
  })

  return branchTreeIdMap
}

function extractOrderedQuestionIds(sections) {
  const ordered = []

  if (Array.isArray(sections)) {
    sections.forEach(section => {
      if (!section || section.visible === false) return
      if (Array.isArray(section.questions)) {
        section.questions.forEach(qid => {
          if (qid) ordered.push(qid)
        })
      }
    })
  }

  return ordered
}

export const buildQuestionMap = template => {
  return template.questions.reduce(
    (acc, question) => ({
      ...acc,
      [question.id]: { ...question }
    }),
    {}
  )
}

function orderBranchTreeMap(branchTreeMap, orderedQuestionIds) {
  const orderedMap = {}

  orderedQuestionIds.forEach(qid => {
    if (qid in branchTreeMap) {
      orderedMap[qid] = branchTreeMap[qid]
    }
  })

  return orderedMap
}

export const parseSurveyResponse = response => {
  const {
    id,
    meta,
    template,
    languages,
    title,
    richTitle,
    type,
    introduction,
    richIntroduction,
    status,
    features
  } = response

  const questionsMap = buildQuestionMap(template)
  const orderedQuestionIds = extractOrderedQuestionIds(template.sections)
  const branchTreeMap = buildBranchTreeIdMap(questionsMap, template?.branchingLogic, orderedQuestionIds)
  const orderedBranchTreeMap = orderBranchTreeMap(branchTreeMap, orderedQuestionIds)

  return {
    status,
    surveyId: id,
    meta,
    title,
    richTitle,
    type,
    introduction,
    richIntroduction,
    template,
    questions: questionsMap,
    languages,
    language: languages[0].code,
    features,
    branchTreeMap: orderedBranchTreeMap
  }
}

export const generateBrandTheme = colour =>
  Utils.createTheme({
    colours: Utils.createColourPalette(colour),
    fonts: createFonts()
  })

export const formatLanguages = languages =>
  languages.reduce((acc, language) => {
    if (!language) return acc
    if (language.code === "en") {
      return [language, ...acc]
    }
    return [...acc, language]
  }, [])

export const castAnswersToString = answers =>
  answers
    .map(answer => {
      if (answer.value && typeof answer.value === "object" && !Array.isArray(answer.value)) {
        const selectedValues = answer.value.values || []
        const otherArr = Array.isArray(answer.value.otherValues) ? answer.value.otherValues : []

        const validOtherValue = otherArr.find(
          ov =>
            ov && ov.optionId != null && ov.value && typeof ov.value === "string" && ov.value.trim() !== ""
        )

        let finalValues = selectedValues.map(val => `${val}`)

        const result = {
          questionId: answer.questionId,
          values: finalValues
        }

        if (
          validOtherValue &&
          validOtherValue.optionId != null &&
          validOtherValue.value &&
          typeof validOtherValue.value === "string" &&
          validOtherValue.value.trim() !== "" &&
          finalValues.includes(`${validOtherValue.optionId}`)
        ) {
          result.otherValues = {
            optionId: validOtherValue.optionId,
            value: validOtherValue.value.trim()
          }
        } else {
          result.otherValues = null
        }

        const hasValues = finalValues.length > 0
        const hasValidOtherValues = result.otherValues !== null
        if (!hasValues && !hasValidOtherValues) {
          return null
        }

        return result
      }

      return {
        questionId: answer.questionId,
        values: Array.isArray(answer.value) ? answer.value.map(val => `${val}`) : [`${answer.value}`]
      }
    })
    .filter(answer => answer !== null)
